import { ScreenOrientation as CapacitorScreenOrientation } from '@capacitor/screen-orientation';
import firebase from "@/utils/Firebase";
import { FCM } from '@capacitor-community/fcm';

import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Device as CapacitorDevice } from '@capacitor/device';
import { Keyboard as CapacitorKeyboard} from '@capacitor/keyboard';


let registrationListener = null
let registrationErrorListener = null
let notificationReceivedListener = null
let notificationActionPerformedListener = null
let localActionPerformedListener = null
let fcmTokenListener = null

let registeredTokenCallback = null
let webMessagingUnsubscribe = null

let deviceInfo = null
let deviceInfoPromise = null

export const Keyboard = {
    async setResizeMode(mode = 'native') {
        if (await Device.isIOS()) {
            await CapacitorKeyboard.setResizeMode({ mode })
        }
    },
}

export const Notifications = {

    pushRequestPermission: async () => {
        try {
            const result = await PushNotifications.requestPermissions()
            return result?.receive || 'prompt'
        } catch (err) {
            console.log('PushNotifications err', JSON.stringify(err));
            return 'denied'
        }
    },

    localRequestPermission: async () => {
        try {
            const result = await LocalNotifications.requestPermissions()
            return result?.display || 'prompt'
        } catch (err) {
            console.log('LocalNotifications err', JSON.stringify(err));
            return 'denied'
        }
    },

    requestPermissions: async () => {
        if (!(await Device.isSmartPhone())) return 'granted'
        const pushPerm = await Notifications.pushRequestPermission()
        await Notifications.localRequestPermission()
        return pushPerm
    },

    async getPermissionState() {
        if (!(await Device.isSmartPhone())) return 'granted'
        try {
            const { receive } = await PushNotifications.checkPermissions()
            return receive
        } catch {
            return 'prompt'
        }
    },

    async initListeners({ onReceived = () => {}, onActionPerformed = () => {}, onLocalActionPerformed = () => {}, onRegisterError = () => {} } = {}) {
        if (!(await Device.isSmartPhone())) return
        try {
            if (!notificationReceivedListener) {
                notificationReceivedListener = PushNotifications.addListener('pushNotificationReceived', onReceived)
            }
            if (!notificationActionPerformedListener) {
                notificationActionPerformedListener = PushNotifications.addListener('pushNotificationActionPerformed', onActionPerformed)
            }
            if (!registrationErrorListener) {
                registrationErrorListener = PushNotifications.addListener('registrationError', onRegisterError)
            }
        } catch (err) {
            console.log('PushNotifications.addListener err', err)
        }
        try {
            if (!localActionPerformedListener) {
                localActionPerformedListener = await LocalNotifications.addListener('localNotificationActionPerformed', onLocalActionPerformed)
            }
        } catch (err) {
            console.log('LocalNotifications.addListener err', err)
        }
    },

    async register(onToken = () => {}) {
        registeredTokenCallback = onToken

        if (!(await Device.isSmartPhone())) {
            return Notifications._registerWeb(onToken)
        }

        const isIOS = await Device.isIOS()

        if (isIOS && !fcmTokenListener) {
            try {
                fcmTokenListener = await FCM.addListener('tokenReceived', ({ token }) => {
                    if (token) registeredTokenCallback?.(token)
                })
            } catch (err) {
                console.log('FCM.addListener err', err)
            }
        }

        if (!registrationListener) {
            registrationListener = PushNotifications.addListener('registration', async (token) => {
                if (isIOS) {
                    try {
                        const r = await FCM.getToken()
                        registeredTokenCallback?.(r.token)
                    } catch (err) {
                        console.log('FCM.getToken err', err)
                    }
                } else {
                    registeredTokenCallback?.(token.value)
                }
            })
        }

        try {
            await PushNotifications.register()
        } catch (err) {
            console.log('PushNotifications.register err', err)
        }
    },

    async getToken() {
        try {
            if (!(await Device.isSmartPhone())) return null
            const result = await FCM.getToken()
            return result?.token || null
        } catch (err) {
            console.log('Notifications.getToken err', err)
            return null
        }
    },

    async _registerWeb(onToken) {
        if (!Device.isStandalone()) {
            console.log('web push notifications are not supported, moving on...')
            return
        }
        const isIos = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase())
        const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent)
        if (isIos || isSafari) return

        try {
            const messaging = firebase.messaging()
            const vapidKey = process.env.VUE_APP_FIREBASE_VAPID_KEY || null
            await messaging.requestPermission()
            const token = await messaging.getToken({ vapidKey })
            onToken(token)
            if (webMessagingUnsubscribe) webMessagingUnsubscribe()
            webMessagingUnsubscribe = messaging.onTokenRefresh(async () => {
                try {
                    const refreshed = await messaging.getToken({ vapidKey })
                    registeredTokenCallback?.(refreshed)
                } catch (err) {
                    console.log('web token refresh err', err)
                }
            })
        } catch (err) {
            console.log('No permission to notify (web):', err);
        }
    },

    async clearListeners() {
        try { await PushNotifications.removeAllListeners() } catch { /* noop */ }
        try { await LocalNotifications.removeAllListeners() } catch { /* noop */ }
        if (fcmTokenListener) {
            try { await fcmTokenListener.remove() } catch { /* noop */ }
            fcmTokenListener = null
        }
        registrationListener = null
        registrationErrorListener = null
        notificationReceivedListener = null
        notificationActionPerformedListener = null
        localActionPerformedListener = null
        if (webMessagingUnsubscribe) {
            webMessagingUnsubscribe()
            webMessagingUnsubscribe = null
        }
        registeredTokenCallback = null
    },

}

export const Device = {
    isSmartPhone: async () => {
        const info = await Device.getInfo()
        return info.platform === 'ios' || info.platform === 'android'
    },

    isIphone: async () => {
        const info = await Device.getInfo()
        return info.model === 'iPhone'
    },

    isIOS: async () => {
        const info = await Device.getInfo()
        return info.platform === 'ios'
    },

    isAndroid: async () => {
        const info = await Device.getInfo()
        return info.platform === 'android'
    },

    isWeb: async () => {
        const info = await Device.getInfo()
        return info.platform === 'web'
    },

    getPlatform: async () => {
        const info = await Device.getInfo()
        return info.platform
    },

    getInfo: async () => {
        if (deviceInfo) return deviceInfo
        if (!deviceInfoPromise) {
            deviceInfoPromise = CapacitorDevice.getInfo()
                .then(info => { deviceInfo = info; return info })
                .catch(err => { deviceInfoPromise = null; throw err })
        }
        return deviceInfoPromise
    },

    getId: async () => {
        try {
            const { identifier } = await CapacitorDevice.getId()
            return identifier
        } catch {
            return null
        }
    },

    getLanguageCode: async () => {
        try {
            const { value } = await CapacitorDevice.getLanguageCode()
            return value
        } catch {
            return null
        }
    },

    isStandalone: () => {
        return (
            window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone ||
            document.referrer.includes('android-app://')
        );
    },
}

export const Screen = {
    lockLandscape: async () => {
        if (!(await Device.isSmartPhone())) return;
        try { await CapacitorScreenOrientation.lock({ orientation: 'landscape' }) } catch (err) { console.log('Screen.lockLandscape err', err) }
    },
    lockPortrait: async () => {
        if (!(await Device.isSmartPhone())) return;
        try { await CapacitorScreenOrientation.lock({ orientation: 'portrait' }) } catch (err) { console.log('Screen.lockPortrait err', err) }
    },
    unlock: async () => {
        if (!(await Device.isSmartPhone())) return;
        try { await CapacitorScreenOrientation.unlock() } catch (err) { console.log('Screen.unlock err', err) }
    },
}
